import { Stack } from '@mui/material'
import { useEffect, useState, Suspense, PropsWithChildren } from 'react'
import { useTranslation } from 'react-i18next'
import { track } from '@omnea-app/fe-analytics/useAnalytics'
import { APP_SCROLLABLE_WRAPPER_ID, mainWrapperContainerName } from '@omnea-app/fe-app-shell/AppLayout'
import { MobileOverlay } from '@omnea-app/fe-app-shell/MobileOverlay'
import { useIsFullyAuthenticated, useIsSupplierRole } from '@omnea-app/fe-auth/UserSessionProvider'
import { RouteAccessGate } from '@omnea-app/fe-authz/RouteAccessGate'
import { useDetectDevice, useIsMobileWidth } from '@omnea-app/fe-browser-utils/useDetectDevice'
import { NotSupportedBanner } from '@omnea-app/fe-legacy/components/shared/ui/mobile-banner/NotSupportedBanner'
import { ReactErrorBoundary } from '@omnea-app/fe-legacy/utils/error-boundary/ReactErrorBoundary'
import { FullWidthLoadingFallback } from '@omnea-app/fe-render-boundaries/LoadingFallbacks'
import { AppShellFrame } from './app-shell/AppShellFrame'
import { chooseFrame } from './app-shell/chooseFrame'
import { useShellVariant } from './app-shell/ShellVariantProvider'
import { Sidebar } from './sidebar/Sidebar'
import { useIsSupportedMobileRoute } from './useIsSupportedMobileRoute'
import type { GatedRoute } from '@omnea-app/fe-authz/RouteAccessGate'

type AppWrapperProps = PropsWithChildren<{
  disableSidebar?: boolean
  sidebarCollapsedByDefault?: boolean
  route: GatedRoute
}>

export const AppWrapper = ({ children, disableSidebar, sidebarCollapsedByDefault, route }: AppWrapperProps) => {
  const { t } = useTranslation('shared')

  const { isMobileAgent, hasMobileEvents, isMobileWidth } = useDetectDevice()

  const isSupportedMobileRoute = useIsSupportedMobileRoute()

  useEffect(() => {
    if (isMobileWidth) {
      track('ViewedOnMobile', {
        isMobileAgent,
        hasMobileEvents,
        screenWidth: window?.screen?.width,
        screenHeight: window?.screen?.height,
        viewportWidth: window?.innerWidth,
        viewportHeight: window?.innerHeight,
      })
    }
  }, [isMobileWidth, hasMobileEvents, isMobileAgent])

  if (!isMobileWidth || isSupportedMobileRoute) {
    return (
      <Stack sx={{ height: '100vh' }} direction="row" data-testid="page-dashboard" overflow="hidden">
        <Wrapper disableSidebar={disableSidebar} sidebarCollapsedByDefault={sidebarCollapsedByDefault} route={route}>
          {children}
        </Wrapper>
      </Stack>
    )
  }

  // For desktop with unsupported width, show overlay over content to prevent rerenders
  if (!isMobileAgent) {
    const translationKey = 'mobile-width-page-not-supported-banner'
    return (
      <Stack sx={{ height: '100vh', position: 'relative' }} direction="row" data-testid="page-dashboard" overflow="hidden">
        <Wrapper disableSidebar={disableSidebar} sidebarCollapsedByDefault={sidebarCollapsedByDefault} route={route}>
          {children}
        </Wrapper>
        <MobileOverlay>
          <NotSupportedBanner title={t(`${translationKey}.title`)} description={t(`${translationKey}.subtitle`)} />
        </MobileOverlay>
      </Stack>
    )
  }

  if (route.pattern === '/') {
    return (
      <MobileOverlay>
        <NotSupportedBanner title={t('mobile-home-banner.title')} description={t('mobile-home-banner.subtitle')} />
      </MobileOverlay>
    )
  }

  const translationKey = 'mobile-agent-page-not-supported-banner'
  return (
    <MobileOverlay>
      <NotSupportedBanner title={t(`${translationKey}.title`)} description={t(`${translationKey}.subtitle`)} />
    </MobileOverlay>
  )
}

const Wrapper = ({ children, disableSidebar, sidebarCollapsedByDefault, route }: AppWrapperProps) => {
  const isAuthenticated = useIsFullyAuthenticated()
  const isSupplier = useIsSupplierRole()
  const isMobileWidth = useIsMobileWidth()
  const shellVariant = useShellVariant()
  const showSidebar = isAuthenticated && !isSupplier && !isMobileWidth && !disableSidebar && shellVariant != null
  const frame = chooseFrame({ shellVariant, isMobileWidth, disableSidebar: disableSidebar === true })
  const [isHydrated, setIsHydrated] = useState(false)

  // Mark as hydrated after initial render to avoid hydration timing issues
  useEffect(() => {
    setIsHydrated(true)
  }, [])

  const renderContent = () => {
    // The pre-hydration pass is committed, so rendering the page there would mount it and fire its
    // queries before the gate can withhold it.
    if (!isHydrated) return isAuthenticated ? <FullWidthLoadingFallback /> : children
    // Until the shell is decided the page waits, so it mounts once, in the frame it keeps.
    if (shellVariant == null) return <FullWidthLoadingFallback />

    return (
      <Suspense fallback={<FullWidthLoadingFallback />}>
        {isAuthenticated ? <RouteAccessGate route={route}>{children}</RouteAccessGate> : children}
      </Suspense>
    )
  }

  if (frame === 'appShell') {
    return (
      <ReactErrorBoundary>
        <AppShellFrame>{renderContent()}</AppShellFrame>
      </ReactErrorBoundary>
    )
  }

  return (
    <ReactErrorBoundary>
      {showSidebar && <Sidebar path={route.pattern} sidebarCollapsedByDefault={sidebarCollapsedByDefault} />}
      <Stack
        width="100%"
        height="100%"
        sx={{
          flex: 1,
          background: 'bg.surface.subtle',

          // This is used by sub-components for container media queries
          // @see https://developer.mozilla.org/en-US/docs/Web/CSS/CSS_containment/Container_queries
          containerName: mainWrapperContainerName,
          containerType: 'inline-size',

          overflowX: 'clip',
          overflowY: 'auto',
        }}
        id={APP_SCROLLABLE_WRAPPER_ID}
      >
        {renderContent()}
      </Stack>
    </ReactErrorBoundary>
  )
}
